import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Button, Table, Badge, Spinner } from 'react-bootstrap'
import { listMyBlogPosts, deleteBlogPost } from '../actions/blogActions'
import { listRssFeeds, updateRssFeed, deleteRssFeed } from '../actions/rssActions'
import Message from '../components/Message'
import RssFeedModal from '../components/RssFeedModal'

function BlogListScreen() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [showRssFeedModal, setShowRssFeedModal] = useState(false)

    const blogMine = useSelector(state => state.blogMine)
    const { loading, error, posts = [] } = blogMine

    const blogDelete = useSelector(state => state.blogDelete)
    const { success: deleteSuccess } = blogDelete

    const rssFeedsList = useSelector(state => state.rssFeedsList)
    const { feeds: rssFeeds = [], loading: feedsLoading } = rssFeedsList

    const rssFeedCreate = useSelector(state => state.rssFeedCreate)
    const { success: feedCreateSuccess } = rssFeedCreate

    const rssFeedDelete = useSelector(state => state.rssFeedDelete)
    const { success: feedDeleteSuccess } = rssFeedDelete

    const userLogin = useSelector(state => state.userLogin)
    const { userInfo } = userLogin

    const isJournalistOrAdmin = userInfo && (userInfo.isAdmin || userInfo.roles?.includes('journalist'))
    const isAdmin = userInfo && userInfo.isAdmin === true

    useEffect(() => {
        if (!isJournalistOrAdmin) {
            navigate('/login')
        } else {
            dispatch(listMyBlogPosts())
            // Fetch RSS feeds if admin
            dispatch(listRssFeeds())
        }
    }, [dispatch, navigate, userInfo, isJournalistOrAdmin, isAdmin, deleteSuccess, feedCreateSuccess, feedDeleteSuccess])

    const handleDeleteBlog = (id) => {
        if (window.confirm('Delete this post?')) {
            dispatch(deleteBlogPost(id))
        }
    }

    const handleToggleRssFeedActive = (id, currentStatus) => {
        dispatch(updateRssFeed(id, !currentStatus))
    }

    const handleDeleteRssFeed = (id) => {
        if (window.confirm('Delete this RSS feed?')) {
            dispatch(deleteRssFeed(id))
        }
    }

    return (
        <div style={{ paddingTop: '1.5rem' }}>
            <div className="d-flex align-items-center justify-content-between mb-4">
                <h4 className="mb-0" style={{ fontWeight: '800', color: 'var(--bnb-navy)' }}>
                    <i className="fa-solid fa-newspaper me-2"></i>My Posts
                </h4>
                <div className="d-flex gap-2">
                    {isAdmin && (
                        <Button
                            onClick={() => setShowRssFeedModal(true)}
                            style={{ background: 'var(--bnb-gold)', border: 'none', fontWeight: '700', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--bnb-navy)' }}
                        >
                            <i className="fa-solid fa-rss me-1"></i> Add RSS Feed
                        </Button>
                    )}
                    <Button
                        onClick={() => navigate('/news/create')}
                        style={{ background: 'var(--bnb-navy)', border: 'none', fontWeight: '700', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}
                    >
                        <i className="fa-solid fa-plus me-1"></i> Write New Post
                    </Button>
                </div>
            </div>

            {loading && (
                <div className="text-center py-4">
                    <Spinner animation="border" size="sm" style={{ color: 'var(--bnb-navy)' }} />
                </div>
            )}
            {error && <Message variant="danger">{error}</Message>}

            {!loading && posts.length === 0 ? (
                <div className="bnb-card p-4 text-center" style={{ color: 'var(--bnb-gray)' }}>
                    <i className="fa-solid fa-file-circle-plus mb-2" style={{ fontSize: '2rem', display: 'block' }}></i>
                    <p className="mb-2">You haven't written any posts yet.</p>
                    <Button
                        size="sm"
                        onClick={() => navigate('/news/create')}
                        style={{ background: 'var(--bnb-navy)', border: 'none', fontWeight: '700' }}
                    >
                        Write Your First Post
                    </Button>
                </div>
            ) : (
                <div className="bnb-card" style={{ overflowX: 'auto' }}>
                    <Table hover style={{ marginBottom: 0, fontSize: '0.875rem' }}>
                        <thead>
                            <tr style={{ background: 'var(--bnb-light)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--bnb-gray)' }}>
                                <th style={{ padding: '0.75rem 1rem', fontWeight: '700', border: 0 }}>Title</th>
                                <th style={{ padding: '0.75rem 1rem', fontWeight: '700', border: 0 }}>Category</th>
                                <th style={{ padding: '0.75rem 1rem', fontWeight: '700', border: 0 }}>Status</th>
                                <th style={{ padding: '0.75rem 1rem', fontWeight: '700', border: 0 }}>Date</th>
                                <th style={{ padding: '0.75rem 1rem', fontWeight: '700', border: 0 }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {posts.map(post => (
                                <tr key={post.id}>
                                    <td style={{ padding: '0.75rem 1rem', fontWeight: '600', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {post.title}
                                    </td>
                                    <td style={{ padding: '0.75rem 1rem' }}>{post.category_display}</td>
                                    <td style={{ padding: '0.75rem 1rem' }}>
                                        <Badge bg={post.is_published ? 'success' : 'secondary'} style={{ fontSize: '0.7rem' }}>
                                            {post.is_published ? 'Published' : 'Draft'}
                                        </Badge>
                                    </td>
                                    <td style={{ padding: '0.75rem 1rem', color: 'var(--bnb-gray)', fontSize: '0.8rem' }}>
                                        {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </td>
                                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            className="me-2"
                                            onClick={() => navigate(`/news/${post.id}/edit`)}
                                            style={{ fontSize: '0.75rem' }}
                                        >
                                            <i className="fa-solid fa-pen"></i>
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline-danger"
                                            onClick={() => handleDeleteBlog(post.id)}
                                            style={{ fontSize: '0.75rem' }}
                                        >
                                            <i className="fa-solid fa-trash"></i>
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}

            {/* RSS Feeds Section - Admin Only */}
            {isAdmin && (
            <div style={{ marginTop: '3rem' }}>
                <h4 className="mb-4" style={{ fontWeight: '800', color: 'var(--bnb-navy)' }}>
                    <i className="fa-solid fa-rss me-2" style={{ color: 'var(--bnb-gold)' }}></i>RSS Feeds
                </h4>

                {feedsLoading && (
                    <div className="text-center py-4">
                        <Spinner animation="border" size="sm" style={{ color: 'var(--bnb-navy)' }} />
                    </div>
                )}

                {!feedsLoading && rssFeeds.length === 0 ? (
                    <div className="bnb-card p-4 text-center" style={{ color: 'var(--bnb-gray)' }}>
                        <i className="fa-solid fa-rss-square mb-2" style={{ fontSize: '2rem', display: 'block' }}></i>
                        <p className="mb-2">No RSS feeds added yet.</p>
                        <Button
                            size="sm"
                            onClick={() => setShowRssFeedModal(true)}
                            style={{ background: 'var(--bnb-navy)', border: 'none', fontWeight: '700' }}
                        >
                            <i className="fa-solid fa-plus me-1"></i>Add Your First Feed
                        </Button>
                    </div>
                ) : (
                    <div className="bnb-card" style={{ overflowX: 'auto' }}>
                        <Table hover style={{ marginBottom: 0, fontSize: '0.875rem' }}>
                            <thead>
                                <tr style={{ background: 'var(--bnb-light)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--bnb-gray)' }}>
                                    <th style={{ padding: '0.75rem 1rem', fontWeight: '700', border: 0 }}>Feed Name</th>
                                    <th style={{ padding: '0.75rem 1rem', fontWeight: '700', border: 0 }}>URL</th>
                                    <th style={{ padding: '0.75rem 1rem', fontWeight: '700', border: 0 }}>Status</th>
                                    <th style={{ padding: '0.75rem 1rem', fontWeight: '700', border: 0 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rssFeeds.map(feed => (
                                    <tr key={feed.id}>
                                        <td style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>
                                            {feed.name}
                                        </td>
                                        <td style={{ padding: '0.75rem 1rem', color: 'var(--bnb-gray)', fontSize: '0.8rem', maxWidth: '350px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {feed.url}
                                        </td>
                                        <td style={{ padding: '0.75rem 1rem' }}>
                                            <Badge
                                                bg={feed.is_active ? 'success' : 'secondary'}
                                                style={{ fontSize: '0.7rem', cursor: 'pointer' }}
                                                onClick={() => handleToggleRssFeedActive(feed.id, feed.is_active)}
                                                title="Click to toggle"
                                            >
                                                {feed.is_active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </td>
                                        <td style={{ padding: '0.75rem 1rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            <Button
                                                size="sm"
                                                variant="outline-danger"
                                                onClick={() => handleDeleteRssFeed(feed.id)}
                                                style={{ fontSize: '0.75rem' }}
                                            >
                                                <i className="fa-solid fa-trash"></i>
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </div>
            )}

            {/* RSS Feed Modal - Admin Only */}
            {isAdmin && <RssFeedModal show={showRssFeedModal} onHide={() => setShowRssFeedModal(false)} />}
        </div>
    )
}

export default BlogListScreen
